/**
 * Custom LPA Terms Form Component
 *
 * Reusable component for editing custom LPA terms that override standard fund terms.
 * Extracted from AddInvestorToFundModal to improve reusability and maintainability.
 */

import { Card, CardContent, CardDescription, CardHeader } from '../ui/card';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../ui/select';
import type { FeeBasisType, FundLPATerms } from '../../types/lpa-terms';
import {
  lpaTermsValidation,
  type ValidationResult,
} from '../../utils/validation';
import { useState, useCallback } from 'react';

export interface CustomLPATermsFormData {
  // Custom Management Fees - Investment Period
  custom_investment_period_fee_rate?: string;
  custom_investment_period_fee_basis_id?: string;

  // Custom Management Fees - Post Investment Period
  custom_post_investment_period_fee_rate?: string;
  custom_post_investment_period_fee_basis_id?: string;

  // Custom Waterfall Structure
  custom_hurdle_rate?: string;
  custom_carried_interest?: string;
}

interface CustomLPATermsFormProps {
  formData: CustomLPATermsFormData;
  onChange: (field: keyof CustomLPATermsFormData, value: string) => void;
  standardTerms?: FundLPATerms; // For showing standard values as placeholders/hints
  feeBasisTypes: FeeBasisType[];
  isLoading?: boolean;
  errors?: Record<string, string>;
  className?: string;
  onValidationChange?: (field: string, isValid: boolean) => void;
}

interface FieldValidation {
  [key: string]: ValidationResult;
}

export function CustomLPATermsForm({
  formData,
  onChange,
  standardTerms,
  feeBasisTypes = [],
  isLoading = false,
  errors = {},
  className,
  onValidationChange,
}: CustomLPATermsFormProps) {
  const [fieldValidation, setFieldValidation] = useState<FieldValidation>({});

  const validateField = useCallback(
    (field: string, value: string): ValidationResult => {
      switch (field) {
        case 'custom_investment_period_fee_rate':
        case 'custom_post_investment_period_fee_rate':
          return lpaTermsValidation.managementFee(value);
        case 'custom_hurdle_rate':
          return lpaTermsValidation.hurdleRate(value);
        case 'custom_carried_interest':
          return lpaTermsValidation.carriedInterest(value);
        default:
          return { isValid: true };
      }
    },
    []
  );

  const handleInputChange = (
    field: keyof CustomLPATermsFormData,
    value: string
  ) => {
    // Format the input for percentage fields
    let formattedValue = value;

    if (
      field.includes('fee_rate') ||
      field === 'custom_hurdle_rate' ||
      field === 'custom_carried_interest'
    ) {
      const formatResult = (() => {
        switch (field) {
          case 'custom_investment_period_fee_rate':
          case 'custom_post_investment_period_fee_rate':
            return lpaTermsValidation.formatManagementFee(value);
          case 'custom_hurdle_rate':
            return lpaTermsValidation.formatHurdleRate(value);
          case 'custom_carried_interest':
            return lpaTermsValidation.formatCarriedInterest(value);
          default:
            return { formattedValue: value, isValid: true };
        }
      })();

      formattedValue = formatResult.formattedValue;

      // Update field validation state
      setFieldValidation((prev) => ({
        ...prev,
        [field]: formatResult,
      }));

      // Notify parent of validation state change
      onValidationChange?.(field, formatResult.isValid);
    } else {
      // For non-percentage fields, just validate
      const validation = validateField(field, value);
      setFieldValidation((prev) => ({
        ...prev,
        [field]: validation,
      }));
      onValidationChange?.(field, validation.isValid);
    }

    onChange(field, formattedValue);
  };

  const getFieldError = (field: string): string | undefined => {
    // Server-side errors take precedence
    if (errors[field]) {
      return errors[field];
    }
    // Then client-side validation errors
    const validation = fieldValidation[field];
    if (validation && !validation.isValid) {
      return validation.error;
    }
    return undefined;
  };

  const hasFieldError = (field: string): boolean => {
    return !!getFieldError(field);
  };

  return (
    <Card className={className}>
      <CardHeader className="pb-4">
        <CardDescription>
          Override standard fund terms for this investor. Standard LPA terms are
          shown as placeholders.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-6">
        {/* Management Fees Section */}
        <div>
          <div className="space-y-4">
            {/* Investment Period Fees */}
            <div>
              <h5 className="text-sm font-medium text-gray-700 mb-2">
                Investment Period
              </h5>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="custom_investment_period_fee_rate">
                    Management Fee %
                  </Label>
                  <Input
                    id="custom_investment_period_fee_rate"
                    type="text"
                    inputMode="decimal"
                    value={formData.custom_investment_period_fee_rate || ''}
                    onChange={(e) =>
                      handleInputChange(
                        'custom_investment_period_fee_rate',
                        e.target.value
                      )
                    }
                    onBlur={(e) => {
                      // Re-validate on blur for immediate feedback
                      const validation = validateField(
                        'custom_investment_period_fee_rate',
                        e.target.value
                      );
                      setFieldValidation((prev) => ({
                        ...prev,
                        custom_investment_period_fee_rate: validation,
                      }));
                    }}
                    placeholder={
                      standardTerms
                        ? `${(standardTerms.investment_period_fee_rate * 100).toFixed(1)}`
                        : '2.0'
                    }
                    className={
                      hasFieldError('custom_investment_period_fee_rate')
                        ? 'border-red-500'
                        : ''
                    }
                    aria-describedby="custom_investment_period_fee_rate-error"
                  />
                  {standardTerms && (
                    <p className="text-xs text-gray-500 mt-1">
                      Standard:{' '}
                      {(standardTerms.investment_period_fee_rate * 100).toFixed(
                        1
                      )}
                      %
                    </p>
                  )}
                  {getFieldError('custom_investment_period_fee_rate') && (
                    <p
                      className="text-sm text-red-600 mt-1"
                      id="custom_investment_period_fee_rate-error"
                    >
                      {getFieldError('custom_investment_period_fee_rate')}
                    </p>
                  )}
                </div>

                <div>
                  <Label htmlFor="custom_investment_period_fee_basis_id">
                    Investment Period Fee Basis
                  </Label>
                  <Select
                    value={formData.custom_investment_period_fee_basis_id || ''}
                    onValueChange={(value) =>
                      handleInputChange(
                        'custom_investment_period_fee_basis_id',
                        value
                      )
                    }
                    disabled={isLoading}
                  >
                    <SelectTrigger id="custom_investment_period_fee_basis_id">
                      <SelectValue
                        placeholder={
                          isLoading ? 'Loading...' : 'Select fee basis'
                        }
                      />
                    </SelectTrigger>
                    <SelectContent>
                      {feeBasisTypes.map((type) => (
                        <SelectItem key={type.id} value={type.id.toString()}>
                          {type.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  {standardTerms?.investment_period_fee_basis && (
                    <p className="text-xs text-gray-500 mt-1">
                      Standard: {standardTerms.investment_period_fee_basis.name}
                    </p>
                  )}
                  {errors.custom_investment_period_fee_basis_id && (
                    <p className="text-sm text-red-600 mt-1">
                      {errors.custom_investment_period_fee_basis_id}
                    </p>
                  )}
                </div>
              </div>
            </div>

            {/* Post-Investment Period Fees */}
            <div>
              <h5 className="text-sm font-medium text-gray-700 mb-2">
                Post-Investment Period
              </h5>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="custom_post_investment_period_fee_rate">
                    Management Fee %
                  </Label>
                  <Input
                    id="custom_post_investment_period_fee_rate"
                    type="text"
                    inputMode="decimal"
                    value={
                      formData.custom_post_investment_period_fee_rate || ''
                    }
                    onChange={(e) =>
                      handleInputChange(
                        'custom_post_investment_period_fee_rate',
                        e.target.value
                      )
                    }
                    onBlur={(e) => {
                      const validation = validateField(
                        'custom_post_investment_period_fee_rate',
                        e.target.value
                      );
                      setFieldValidation((prev) => ({
                        ...prev,
                        custom_post_investment_period_fee_rate: validation,
                      }));
                    }}
                    placeholder={
                      standardTerms
                        ? `${(standardTerms.post_investment_period_fee_rate * 100).toFixed(1)}`
                        : '1.5'
                    }
                    className={
                      hasFieldError('custom_post_investment_period_fee_rate')
                        ? 'border-red-500'
                        : ''
                    }
                    aria-describedby="custom_post_investment_period_fee_rate-error"
                  />
                  {standardTerms && (
                    <p className="text-xs text-gray-500 mt-1">
                      Standard:{' '}
                      {(
                        standardTerms.post_investment_period_fee_rate * 100
                      ).toFixed(1)}
                      %
                    </p>
                  )}
                  {getFieldError('custom_post_investment_period_fee_rate') && (
                    <p
                      className="text-sm text-red-600 mt-1"
                      id="custom_post_investment_period_fee_rate-error"
                    >
                      {getFieldError('custom_post_investment_period_fee_rate')}
                    </p>
                  )}
                </div>

                <div>
                  <Label htmlFor="custom_post_investment_period_fee_basis_id">
                    Period Fee Basis
                  </Label>
                  <Select
                    value={
                      formData.custom_post_investment_period_fee_basis_id || ''
                    }
                    onValueChange={(value) =>
                      handleInputChange(
                        'custom_post_investment_period_fee_basis_id',
                        value
                      )
                    }
                    disabled={isLoading}
                  >
                    <SelectTrigger id="custom_post_investment_period_fee_basis_id">
                      <SelectValue
                        placeholder={
                          isLoading ? 'Loading...' : 'Select fee basis'
                        }
                      />
                    </SelectTrigger>
                    <SelectContent>
                      {feeBasisTypes.map((type) => (
                        <SelectItem key={type.id} value={type.id.toString()}>
                          {type.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  {standardTerms?.post_investment_period_fee_basis && (
                    <p className="text-xs text-gray-500 mt-1">
                      Standard:{' '}
                      {standardTerms.post_investment_period_fee_basis.name}
                    </p>
                  )}
                  {errors.custom_post_investment_period_fee_basis_id && (
                    <p className="text-sm text-red-600 mt-1">
                      {errors.custom_post_investment_period_fee_basis_id}
                    </p>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <Label htmlFor="custom_hurdle_rate">Hurdle Rate %</Label>
            <Input
              id="custom_hurdle_rate"
              type="text"
              inputMode="decimal"
              value={formData.custom_hurdle_rate || ''}
              onChange={(e) =>
                handleInputChange('custom_hurdle_rate', e.target.value)
              }
              onBlur={(e) => {
                const validation = validateField(
                  'custom_hurdle_rate',
                  e.target.value
                );
                setFieldValidation((prev) => ({
                  ...prev,
                  custom_hurdle_rate: validation,
                }));
              }}
              placeholder={
                standardTerms
                  ? `${(standardTerms.hurdle_rate * 100).toFixed(1)}`
                  : '8.0'
              }
              className={
                hasFieldError('custom_hurdle_rate') ? 'border-red-500' : ''
              }
              aria-describedby="custom_hurdle_rate-error"
            />
            {standardTerms && (
              <p className="text-xs text-gray-500 mt-1">
                Standard: {(standardTerms.hurdle_rate * 100).toFixed(1)}%
              </p>
            )}
            {getFieldError('custom_hurdle_rate') && (
              <p
                className="text-sm text-red-600 mt-1"
                id="custom_hurdle_rate-error"
              >
                {getFieldError('custom_hurdle_rate')}
              </p>
            )}
          </div>

          <div>
            <Label htmlFor="custom_carried_interest">Carried Interest %</Label>
            <Input
              id="custom_carried_interest"
              type="text"
              inputMode="decimal"
              value={formData.custom_carried_interest || ''}
              onChange={(e) =>
                handleInputChange('custom_carried_interest', e.target.value)
              }
              onBlur={(e) => {
                const validation = validateField(
                  'custom_carried_interest',
                  e.target.value
                );
                setFieldValidation((prev) => ({
                  ...prev,
                  custom_carried_interest: validation,
                }));
              }}
              placeholder={
                standardTerms
                  ? `${(standardTerms.carried_interest * 100).toFixed(1)}`
                  : '20.0'
              }
              className={
                hasFieldError('custom_carried_interest') ? 'border-red-500' : ''
              }
              aria-describedby="custom_carried_interest-error"
            />
            {standardTerms && (
              <p className="text-xs text-gray-500 mt-1">
                Standard: {(standardTerms.carried_interest * 100).toFixed(1)}%
              </p>
            )}
            {getFieldError('custom_carried_interest') && (
              <p
                className="text-sm text-red-600 mt-1"
                id="custom_carried_interest-error"
              >
                {getFieldError('custom_carried_interest')}
              </p>
            )}
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
